"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Trophy } from "lucide-react";

interface TopPrinter {
  name: string;
  email: string;
  count: number;
  initials: string;
}

interface ProductivityData {
  topPrinters: TopPrinter[];
  totalItems: number;
}

interface DailyProductivitySummaryProps {
  accountId?: string | null;
}

export function DailyProductivitySummary({ accountId }: DailyProductivitySummaryProps) {
  const [data, setData] = useState<ProductivityData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProductivity();
  }, [accountId]);

  const fetchProductivity = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (accountId) {
        params.append("accountId", accountId);
      }

      const response = await fetch(`/api/daily-productivity?${params}`);
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        console.error("Failed to fetch daily productivity:", result.error);
        setData(null);
      }
    } catch (error) {
      console.error("Error fetching daily productivity:", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const getAvatarColor = (initials: string) => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-orange-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-red-500",
      "bg-yellow-500",
    ];
    const index = initials.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const getRankIcon = (index: number) => {
    if (index === 0) return "🥇";
    if (index === 1) return "🥈";
    if (index === 2) return "🥉";
    return "";
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base sm:text-lg">Productivity Leaderboard</CardTitle>
            <CardDescription className="text-xs sm:text-sm">Top teammates for today</CardDescription>
          </div>
          <div className="px-3 py-1 bg-slate-100 rounded-md self-start sm:self-auto">
            <span className="text-xs sm:text-sm font-medium text-slate-700">Today</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex flex-col items-center justify-center p-4 sm:p-8">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400 mb-2" />
            <span className="text-xs sm:text-sm text-slate-500">Loading productivity data...</span>
          </div>
        ) : data && data.topPrinters.length > 0 ? (
          <div className="space-y-2 sm:space-y-3">
            {/* Top 3 Printers */}
            {data.topPrinters.map((printer, index) => (
              <div
                key={printer.email}
                className="flex items-center justify-between p-2 sm:p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  {/* Rank Medal */}
                  <span className="text-xl sm:text-2xl flex-shrink-0">{getRankIcon(index)}</span>
                  
                  {/* Avatar */}
                  <div
                    className={`h-8 w-8 sm:h-10 sm:w-10 rounded-full ${getAvatarColor(printer.initials)} flex items-center justify-center text-white font-semibold text-xs sm:text-sm flex-shrink-0`}
                  >
                    {printer.initials}
                  </div>
                  
                  {/* User Name */}
                  <span className="font-medium text-gray-900 text-sm sm:text-base truncate">{printer.name}</span>
                </div>
                
                {/* Count Badge */}
                <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                  <span className="text-xl sm:text-2xl font-bold text-gray-900">{printer.count}</span>
                  <span className="text-xs sm:text-sm text-gray-500">items</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-4 sm:p-8">
            <Trophy className="h-12 w-12 text-slate-300 mb-2" />
            <p className="text-xs sm:text-sm text-slate-500">No printing activity today</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
